import { isNil } from 'lodash';
import { toPercentage, toSignificant } from '@/utilities/formatting';
import DataTable from '@components/DataTable';

interface TwoStageFCFModelProps {
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  reportingCurrencySymbol: string;
  companyShortName: string;
  relativeListingSharesOutstanding: number;
  riskFreeRate: number;
  costOfEquity: number;
  pv5y: number;
  terminalValue: number;
  pvtv: number;
  npv: number;
  npvPerShare: number;
  npvPerShareReportedCurrency?: number;
  twoStagefcf: {
    affoEstimates?: number;
    growthCAGR5y: number;
    scale: string;
    scaleNumeric: number;
    sharesOutstanding: number;
    fcfsource?: string;
    fcfData: { date: number; value: number }[];
    fcfCount: { date: number; value: number }[];
    fcfLtm: number;
    firstStage: {
      year: number;
      data: number;
      source: string;
      discounted: number;
    }[];
  };
}

const TwoStageFCFModel = ({
  uniqueSymbol,
  reportingCurrencyISO,
  reportingCurrencySymbol,
  companyShortName,
  relativeListingSharesOutstanding,
  riskFreeRate,
  costOfEquity,
  pv5y,
  terminalValue,
  pvtv,
  npv,
  twoStagefcf: { affoEstimates, scale, fcfsource, firstStage },
}: TwoStageFCFModelProps) => (
  <div>
    <p>
      {`The calculations below outline how an intrinsic value for ${companyShortName} is arrived at by discounting future cash flows to their present value using the 2 stage method. We use analyst's estimates of cash flows going forward 10 years for the 1st stage, the 2nd stage assumes the company grows at a stable rate into perpetuity.`}
    </p>
    {!isNil(affoEstimates) && (
      <p>
        {`${companyShortName} is a Real Estate Investment Trust (REIT), we use funds from operations (FFO) or adjusted funds from operations (AFFO) instead of levered free cash flow for REITs. This excludes depreciation and borrowing. Ideally analysts estimates of AFFO are used, where these aren't available we use FFO.`}
      </p>
    )}
    <DataTable light>
      <caption>
        {`${uniqueSymbol} DCF 1st Stage: Next 10 years cash flow forecast`}
      </caption>
      <thead>
        <tr>
          <th />
          <th>
            {`${
              !isNil(fcfsource) ? fcfsource : 'Levered FCF'
            } (${reportingCurrencyISO}, ${scale})`}
          </th>
          <th>Source</th>
          <th>
            <span>Present Value </span>
            <br />
            <span>{`  Discounted (@ ${toPercentage(costOfEquity, 2)})`}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {firstStage.map((stageData) => (
          <tr key={stageData.year.toString()}>
            <td>
              <strong>{stageData.year}</strong>
            </td>
            <td>
              {parseFloat(toSignificant(stageData.data, 2)).toLocaleString()}
            </td>
            <td>{stageData.source}</td>
            <td>
              {parseFloat(
                toSignificant(stageData.discounted, 2)
              ).toLocaleString()}
            </td>
          </tr>
        ))}
        <tr>
          <td colSpan={3}>
            <strong>Present value of next 10 years cash flows</strong>
          </td>
          <td>
            {`${reportingCurrencySymbol}${parseFloat(
              toSignificant(pv5y, 2)
            ).toLocaleString()}`}
          </td>
        </tr>
      </tbody>
    </DataTable>
    <p> </p>
    {!isNil(terminalValue) && (
      <DataTable light>
        <caption>{`${uniqueSymbol} DCF 2nd Stage: Terminal Value`}</caption>
        <thead>
          <tr>
            <th />
            <th>Calculation</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Terminal Value</td>
            <td>
              <span>FCF</span>
              <sub>{firstStage[firstStage.length - 1].year}</sub>
              <span> × (1 + g) ÷ (Discount Rate – g)</span>
              <br />
              <span>{`= ${reportingCurrencySymbol}${firstStage[
                firstStage.length - 1
              ].data.toLocaleString()} x (1 + ${toPercentage(
                riskFreeRate,
                2
              )}) ÷ (${toPercentage(costOfEquity, 2)} - ${toPercentage(
                riskFreeRate,
                2
              )} )`}</span>
            </td>
            <td>
              {`${reportingCurrencySymbol}${parseFloat(
                toSignificant(terminalValue, 2)
              ).toLocaleString()}`}
            </td>
          </tr>
          <tr>
            <td>Present Value of Terminal Value</td>
            <td>
              <span>= Terminal Value ÷ (1 + r)</span>
              <sup>10</sup>
              <br />
              <span>{`${reportingCurrencySymbol}${parseFloat(
                toSignificant(terminalValue, 0)
              ).toLocaleString()} ÷ (1 + ${toPercentage(
                costOfEquity,
                2
              )})`}</span>
              <sup>10</sup>
            </td>
            <td>
              {`${reportingCurrencySymbol}${parseFloat(
                toSignificant(pvtv, 2)
              ).toLocaleString()}`}
            </td>
          </tr>
        </tbody>
      </DataTable>
    )}
    <p> </p>
    <DataTable light>
      <caption>{`${uniqueSymbol} Total Equity Value`}</caption>
      <thead>
        <tr>
          <th />
          <th>Calculation</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Total Equity Value</td>
          <td>
            <span>
              = Present value of next 10 years cash flows + Terminal Value
            </span>
            <br />
            <span>{`= ${reportingCurrencySymbol}${parseFloat(
              toSignificant(pv5y, 0)
            ).toLocaleString()} + ${reportingCurrencySymbol}${parseFloat(
              toSignificant(pvtv, 0)
            ).toLocaleString()}`}</span>
          </td>
          <td>
            {`${reportingCurrencySymbol}${parseFloat(
              toSignificant(npv, 2)
            ).toLocaleString()}`}
          </td>
        </tr>
        <tr>
          <td>
            <span>Equity Value per Share</span>
            <br />
            <span>{`(${reportingCurrencyISO})`}</span>
          </td>
          <td>
            <span>= Total value / Shares Outstanding</span>
            <br />
            <span>
              {`= ${reportingCurrencySymbol}${parseFloat(
                toSignificant(npv, 0)
              ).toLocaleString()} / ${parseFloat(
                toSignificant(relativeListingSharesOutstanding, 0)
              ).toLocaleString()}`}
            </span>
          </td>
          <td>
            {`${reportingCurrencySymbol}${parseFloat(
              toSignificant(npv / relativeListingSharesOutstanding, 2)
            ).toLocaleString()}`}
          </td>
        </tr>
      </tbody>
    </DataTable>
  </div>
);

export default TwoStageFCFModel;
